import { apiFetch } from '../utils/api'
import { useMemo, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Link2, PlugZap, ShieldCheck, RefreshCw, Trash2, Clock } from 'lucide-react'
import { downloadJson } from '../utils/exportJson'
import QuickBooksStatusCard from './QuickBooksStatusCard'

// Developer-only gate for the BYO-token form and active sync/test affordances.
// Shares the same `bluaiq_dev_mode` localStorage key + `?dev=1` query toggle
// already used by takeoff-diagnostic-v3 so a single dev signal unlocks all
// internal preview surfaces. Normal beta users never see active connect/test
// affordances or token entry — Google OAuth and provider sync jobs are not
// implemented in production yet.
function isIntegrationsDevPreview(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('dev') === '1') {
      try { window.localStorage.setItem('bluaiq_dev_mode', '1') } catch (_) { /* ignore */ }
      return true
    }
  } catch (_) { /* ignore */ }
  try {
    if (window.localStorage.getItem('bluaiq_dev_mode') === '1') return true
  } catch (_) { /* ignore */ }
  try {
    const meta = import.meta as unknown as { env?: Record<string, string | undefined> }
    return meta?.env?.VITE_TAKEOFF_DIAGNOSTIC_V3_ENABLED === 'true'
  } catch (_) {
    return false
  }
}

type Provider =
  | 'quickbooks'
  | 'ramp'
  | 'xero'
  | 'sage_intacct'
  | 'clickup'
  | 'google_sheets'
  | 'excel'

interface IntegrationRecord {
  id: string
  provider: Provider
  type: 'ACCOUNTING' | 'SPEND' | 'PM' | 'EXPORT'
  externalId?: string | null
  workspaceId?: string | null
  mapping?: Record<string, unknown> | null
  metadata?: Record<string, unknown> | null
  lastSyncAt?: string | null
  lastTestAt?: string | null
  status: string
  isActive: boolean
  createdAt: string
}

interface IntegrationFormState {
  provider: Provider
  type: 'ACCOUNTING' | 'SPEND' | 'PM' | 'EXPORT'
  accessToken: string
  refreshToken: string
  externalId: string
  workspaceId: string
  mappingJson: string
  metadataJson: string
}

interface AccountingPreview {
  provider: 'quickbooks' | 'xero' | 'sage_intacct'
  budgetTotal: number
  budgetLineCount: number
  approvedChangeOrderTotal: number
  latestDrawNumber: number | null
  latestDrawAmount: number
  pendingPaymentTotal: number
  paidPaymentTotal: number
  sampleBudgetLines: Array<{ category: string; label: string; amount: number }>
}

interface RampPreview {
  provider: 'ramp'
  receiptCount: number
  receiptSpendTotal: number
  topMerchants: Array<{ merchant: string; total: number }>
  topCategories: Array<{ category: string; total: number }>
  quoteSignals: Array<{ category: string; avgUnitPrice: number; quoteCount: number }>
}

interface IntegrationPreviewPayload {
  quickbooks: AccountingPreview
  xero: AccountingPreview
  sageIntacct: AccountingPreview
  ramp: RampPreview
}

const PROVIDERS: Array<{
  provider: Provider
  type: 'ACCOUNTING' | 'SPEND' | 'PM' | 'EXPORT'
  nameEn: string
  nameEs: string
  summaryEn: string
  summaryEs: string
  priority: 'now' | 'next' | 'later'
}> = [
  {
    provider: 'quickbooks',
    type: 'ACCOUNTING',
    nameEn: 'QuickBooks Online',
    nameEs: 'QuickBooks Online',
    summaryEn: 'Accounting sync for project actuals, bills, and payment visibility.',
    summaryEs: 'Sync contable para reales de proyecto, cuentas por pagar y visibilidad de pagos.',
    priority: 'now',
  },
  {
    provider: 'ramp',
    type: 'SPEND',
    nameEn: 'Ramp',
    nameEs: 'Ramp',
    summaryEn: 'Spend data and receipt flow without rebuilding cards or AP.',
    summaryEs: 'Datos de gasto y recibos sin reconstruir tarjetas ni AP.',
    priority: 'now',
  },
  {
    provider: 'sage_intacct',
    type: 'ACCOUNTING',
    nameEn: 'Sage Intacct',
    nameEs: 'Sage Intacct',
    summaryEn: 'Later connector for builders with larger accounting stacks.',
    summaryEs: 'Conector posterior para builders con stacks contables más grandes.',
    priority: 'next',
  },
  {
    provider: 'xero',
    type: 'ACCOUNTING',
    nameEn: 'Xero',
    nameEs: 'Xero',
    summaryEn: 'Optional accounting connector where customers already use Xero.',
    summaryEs: 'Conector contable opcional cuando los clientes ya usan Xero.',
    priority: 'next',
  },
  {
    provider: 'clickup',
    type: 'PM',
    nameEn: 'ClickUp',
    nameEs: 'ClickUp',
    summaryEn: 'Legacy project-management export path.',
    summaryEs: 'Ruta legacy de exportación hacia project management.',
    priority: 'later',
  },
  {
    provider: 'google_sheets',
    type: 'EXPORT',
    nameEn: 'Google Sheets',
    nameEs: 'Google Sheets',
    summaryEn: 'Export bridge for external spreadsheet workflows.',
    summaryEs: 'Puente de exportación para flujos externos en hojas de cálculo.',
    priority: 'later',
  },
  {
    provider: 'excel',
    type: 'EXPORT',
    nameEn: 'Excel',
    nameEs: 'Excel',
    summaryEn: 'Lightweight export-oriented connection.',
    summaryEs: 'Conexión ligera orientada a exportación.',
    priority: 'later',
  },
]

const EMPTY_FORM: IntegrationFormState = {
  provider: 'quickbooks',
  type: 'ACCOUNTING',
  accessToken: '',
  refreshToken: '',
  externalId: '',
  workspaceId: '',
  mappingJson: '',
  metadataJson: '',
}

function safeParseJson(value: string): Record<string, unknown> | null {
  if (!value.trim()) return null
  return JSON.parse(value) as Record<string, unknown>
}

export default function IntegrationsModule() {
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const { projectId } = useParams<{ projectId: string }>()
  const isEs = i18n.language === 'es'
  const queryClient = useQueryClient()

  const devPreview = useMemo(() => isIntegrationsDevPreview(), [])

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<IntegrationRecord | null>(null)
  const [form, setForm] = useState<IntegrationFormState>(EMPTY_FORM)
  const [notice, setNotice] = useState<string | null>(null)

  const showNotice = (message: string) => {
    setNotice(message)
    setTimeout(() => setNotice(null), 3000)
  }

  const { data: integrations = [] } = useQuery<IntegrationRecord[]>({
    queryKey: ['integrations'],
    enabled: devPreview,
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch('/api/integrations', {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('INTEGRATIONS_LOAD_FAILED')
      return res.json()
    },
    retry: false,
  })

  const { data: previews } = useQuery<IntegrationPreviewPayload>({
    queryKey: ['integration-previews', projectId],
    enabled: devPreview && !!projectId,
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/integrations/previews`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('INTEGRATION_PREVIEW_LOAD_FAILED')
      return res.json()
    },
    retry: false,
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const payload = {
        provider: form.provider,
        type: form.type,
        accessToken: form.accessToken,
        refreshToken: form.refreshToken || undefined,
        externalId: form.externalId || undefined,
        workspaceId: form.workspaceId || undefined,
        mapping: safeParseJson(form.mappingJson),
        metadata: safeParseJson(form.metadataJson),
      }

      const url = editing ? `/api/integrations/${editing.id}` : '/api/integrations'
      const method = editing ? 'PATCH' : 'POST'
      const res = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'INTEGRATION_SAVE_FAILED')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      setShowForm(false)
      setEditing(null)
      setForm(EMPTY_FORM)
      showNotice(isEs ? 'Integración guardada' : 'Integration saved')
    },
    onError: (error: Error) => showNotice(error.message),
  })

  const testMutation = useMutation({
    mutationFn: async (provider: Provider) => {
      const token = await getToken()
      const res = await apiFetch(`/api/integrations/${provider}/test`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      return res.json()
    },
    onSuccess: (result: { connected: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      showNotice(result.connected ? (isEs ? 'Conexión verificada' : 'Connection verified') : (isEs ? 'Conexión fallida' : 'Connection failed'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken()
      const res = await apiFetch(`/api/integrations/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('INTEGRATION_DELETE_FAILED')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      showNotice(isEs ? 'Integración desconectada' : 'Integration disconnected')
    },
  })

  const exportPackageMutation = useMutation({
    mutationFn: async (provider: 'quickbooks' | 'ramp' | 'xero' | 'sage_intacct') => {
      if (!projectId) throw new Error('PROJECT_REQUIRED')
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/integrations/export-package?provider=${provider}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('INTEGRATION_EXPORT_FAILED')
      return { provider, payload: await res.json() }
    },
    onSuccess: ({ provider, payload }) => {
      downloadJson(`${provider}-${projectId}-export-package`, payload)
      showNotice(isEs ? 'Paquete exportado' : 'Export package downloaded')
    },
    onError: (error: Error) => showNotice(error.message),
  })

  const connectedByProvider = useMemo(() => {
    const map = new Map<Provider, IntegrationRecord>()
    for (const integration of integrations) {
      map.set(integration.provider, integration)
    }
    return map
  }, [integrations])

  const previewByProvider = useMemo(() => ({
    quickbooks: previews?.quickbooks,
    ramp: previews?.ramp,
    xero: previews?.xero,
    sage_intacct: previews?.sageIntacct,
  }), [previews])

  const getPreviewForProvider = (provider: Provider) => {
    switch (provider) {
      case 'quickbooks':
        return previewByProvider.quickbooks
      case 'ramp':
        return previewByProvider.ramp
      case 'xero':
        return previewByProvider.xero
      case 'sage_intacct':
        return previewByProvider.sage_intacct
      default:
        return undefined
    }
  }

  const openCreate = (provider: Provider, type: IntegrationFormState['type']) => {
    setEditing(null)
    setForm({
      ...EMPTY_FORM,
      provider,
      type,
    })
    setShowForm(true)
  }

  const openEdit = (integration: IntegrationRecord) => {
    setEditing(integration)
    setForm({
      provider: integration.provider,
      type: integration.type,
      accessToken: '',
      refreshToken: '',
      externalId: integration.externalId || '',
      workspaceId: integration.workspaceId || '',
      mappingJson: integration.mapping ? JSON.stringify(integration.mapping, null, 2) : '',
      metadataJson: integration.metadata ? JSON.stringify(integration.metadata, null, 2) : '',
    })
    setShowForm(true)
  }

  return (
    <div className="p-5 max-w-6xl mx-auto space-y-4 relative">
      {notice && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white text-[13px] font-[800] px-4 py-2 rounded-full shadow-lg">
          {notice}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
            <Link2 size={18} />
            {isEs ? 'Integraciones' : 'Integrations'}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {isEs
              ? 'Conecta NATAMI con tu stack financiero sin convertirnos en ERP.'
              : 'Connect NATAMI to your finance stack without turning it into an ERP.'}
          </p>
        </div>
      </div>

      {!devPreview && (
        <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3">
          <div className="flex items-center gap-2 text-[10px] font-[800] uppercase tracking-[0.4px] text-[#92400E]">
            <Clock size={12} />
            {isEs ? 'Próximamente' : 'Coming soon'}
          </div>
          <p className="mt-1 text-[12px] font-[600] text-[#4B5563]">
            {isEs
              ? 'Las integraciones estarán disponibles próximamente. Google Calendar, Gmail, Drive/Sheets, QuickBooks y otros proveedores estarán disponibles después del endurecimiento beta.'
              : 'Integrations are coming soon. Google Calendar, Gmail, Drive/Sheets, QuickBooks, and other provider syncs will be available after beta hardening.'}
          </p>
          <p className="mt-2 text-[11px] font-[600] text-[#6B7280]">
            {isEs
              ? 'Durante beta, NATAMI mantiene los datos del proyecto dentro de la app. Las sincronizaciones con proveedores se activarán solo después de completar OAuth, seguridad de tokens y revisión de permisos.'
              : 'For beta, NATAMI keeps your project data inside the app. Provider syncs will be activated only after OAuth, token security, and permission review are complete.'}
          </p>
        </div>
      )}

      {devPreview && (
        <div className="rounded-xl border border-[#FDBA74] bg-[#FFF7ED] px-4 py-3">
          <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9A3412]">
            {isEs ? 'Vista previa para desarrollo' : 'Developer preview'}
          </div>
          <p className="mt-1 text-[12px] font-[600] text-[#4B5563]">
            {isEs
              ? 'Vista previa para desarrollo — no disponible para usuarios beta.'
              : 'Developer preview only — not available to beta users.'}
          </p>
        </div>
      )}

      {/* QB-1 — read-only QuickBooks Online connection status + safety panel.
          No OAuth, no Intuit API calls, no sync; QuickBooks real sync stays BLOCKED. */}
      <QuickBooksStatusCard isEs={isEs} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {PROVIDERS.map(provider => {
          const connected = connectedByProvider.get(provider.provider)
          const priorityLabel = provider.priority === 'now'
            ? (isEs ? 'Ahora' : 'Now')
            : provider.priority === 'next'
              ? (isEs ? 'Siguiente' : 'Next')
              : (isEs ? 'Después' : 'Later')

          return (
            <div key={provider.provider} className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[13px] font-[900] text-[#1C1A17]">
                    {isEs ? provider.nameEs : provider.nameEn}
                  </div>
                  <div className="text-[10px] font-[700] text-[#9E9890] mt-0.5">
                    {provider.type}
                  </div>
                </div>
                {devPreview && (
                  <span className={`text-[9px] font-[800] px-2 py-1 rounded-full ${
                    provider.priority === 'now'
                      ? 'bg-[#ECFDF5] text-[#047857]'
                      : provider.priority === 'next'
                        ? 'bg-[#EFF6FF] text-[#06539B]'
                        : 'bg-[#F8FAFC] text-[#64748B]'
                  }`}>
                    {priorityLabel}
                  </span>
                )}
              </div>

              <p className="text-[11px] font-[600] text-[#4B5563]">
                {isEs ? provider.summaryEs : provider.summaryEn}
              </p>

              <div className="flex items-center gap-2 text-[11px] font-[700]">
                {!devPreview ? (
                  <span className="px-2.5 py-1 rounded-full bg-[#FFFBEB] text-[#92400E] flex items-center gap-1">
                    <Clock size={11} />
                    {isEs ? 'Próximamente' : 'Coming soon'}
                  </span>
                ) : (
                  <>
                    <span className={`px-2.5 py-1 rounded-full ${
                      connected ? 'bg-[#ECFDF5] text-[#047857]' : 'bg-[#F3F4F6] text-[#6B7280]'
                    }`}>
                      {connected ? (isEs ? 'Conectado' : 'Connected') : (isEs ? 'No conectado' : 'Not connected')}
                    </span>
                    {connected?.lastSyncAt && (
                      <span className="text-[#9E9890]">
                        {isEs ? 'Sync' : 'Sync'}: {new Date(connected.lastSyncAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}
                      </span>
                    )}
                  </>
                )}
              </div>

              {devPreview && connected && (
                <div className="rounded-lg bg-[#FAFAF8] border border-[#ECEAE4] px-3 py-2 text-[10px] font-[600] text-[#4B5563] space-y-1">
                  {connected.workspaceId && <div>workspace: {connected.workspaceId}</div>}
                  {connected.externalId && <div>external: {connected.externalId}</div>}
                  {connected.lastTestAt && (
                    <div>{isEs ? 'Último test' : 'Last test'}: {new Date(connected.lastTestAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}</div>
                  )}
                </div>
              )}

              {devPreview && getPreviewForProvider(provider.provider) && (
                <div className="rounded-lg border border-[#DBEAFE] bg-[#F8FBFF] px-3 py-2 text-[10px] font-[600] text-[#355070] space-y-1">
                  <div className="text-[9px] font-[800] uppercase tracking-[0.3px] text-[#06539B]">
                    {isEs ? 'Preview del sync' : 'Sync preview'}
                  </div>
                  {provider.provider === 'ramp'
                    ? (() => {
                        const rampPreview = getPreviewForProvider(provider.provider) as RampPreview
                        return (
                          <>
                            <div>{isEs ? 'Gasto en recibos' : 'Receipt spend'}: ${rampPreview.receiptSpendTotal.toLocaleString()}</div>
                            <div>{isEs ? 'Recibos' : 'Receipts'}: {rampPreview.receiptCount}</div>
                            {rampPreview.topMerchants[0] && (
                              <div>{isEs ? 'Comercio principal' : 'Top merchant'}: {rampPreview.topMerchants[0].merchant}</div>
                            )}
                            {rampPreview.quoteSignals[0] && (
                              <div>{isEs ? 'Señal de cotizaciones' : 'Quote signal'}: {rampPreview.quoteSignals[0].category}</div>
                            )}
                          </>
                        )
                      })()
                    : (() => {
                        const accountingPreview = getPreviewForProvider(provider.provider) as AccountingPreview
                        return (
                          <>
                            <div>{isEs ? 'Presupuesto' : 'Budget'}: ${accountingPreview.budgetTotal.toLocaleString()}</div>
                            <div>{isEs ? 'Cambios aprobados' : 'Approved COs'}: ${accountingPreview.approvedChangeOrderTotal.toLocaleString()}</div>
                            <div>{isEs ? 'Pagos pendientes' : 'Pending payments'}: ${accountingPreview.pendingPaymentTotal.toLocaleString()}</div>
                            {accountingPreview.latestDrawNumber && (
                              <div>{isEs ? 'Última draw' : 'Latest draw'}: #{accountingPreview.latestDrawNumber}</div>
                            )}
                          </>
                        )
                      })()}
                </div>
              )}

              {devPreview ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => connected ? openEdit(connected) : openCreate(provider.provider, provider.type)}
                    className="flex items-center gap-1.5 bg-[#06539B] hover:bg-[#14457E] text-white text-[11px] font-[800] px-3 py-2 rounded-full transition-colors"
                  >
                    <PlugZap size={12} />
                    {connected ? (isEs ? 'Editar' : 'Edit') : (isEs ? 'Conectar' : 'Connect')}
                  </button>
                  {connected && (
                    <>
                      <button
                        onClick={() => testMutation.mutate(provider.provider)}
                        className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[11px] font-[800] px-3 py-2 rounded-full hover:bg-[#F2F0EB] transition-colors"
                      >
                        <ShieldCheck size={12} />
                        {isEs ? 'Probar' : 'Test'}
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(connected.id)}
                        className="flex items-center gap-1.5 border border-[#FECACA] text-[#B91C1C] text-[11px] font-[800] px-3 py-2 rounded-full hover:bg-[#FEF2F2] transition-colors"
                      >
                        <Trash2 size={12} />
                        {isEs ? 'Desconectar' : 'Disconnect'}
                      </button>
                    </>
                  )}
                  {(provider.provider === 'quickbooks' || provider.provider === 'ramp' || provider.provider === 'xero' || provider.provider === 'sage_intacct') && projectId && (
                    <button
                      onClick={() => exportPackageMutation.mutate(provider.provider as 'quickbooks' | 'ramp' | 'xero' | 'sage_intacct')}
                      disabled={exportPackageMutation.isPending}
                      className="flex items-center gap-1.5 border border-[#BFDBFE] text-[#06539B] text-[11px] font-[800] px-3 py-2 rounded-full hover:bg-[#EFF6FF] transition-colors disabled:opacity-40"
                    >
                      <RefreshCw size={12} />
                      {isEs ? 'Exportar paquete' : 'Export package'}
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  title={isEs ? 'Próximamente' : 'Coming soon'}
                  className="flex items-center gap-1.5 bg-[#F3F4F6] text-[#9CA3AF] text-[11px] font-[800] px-3 py-2 rounded-full cursor-not-allowed"
                >
                  <Clock size={12} />
                  {isEs ? 'Próximamente' : 'Coming soon'}
                </button>
              )}
            </div>
          )
        })}
      </div>

      {devPreview && showForm && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
          <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9A3412]">
            {isEs ? 'Vista previa para desarrollo — no disponible para usuarios beta' : 'Developer preview only — not available to beta users'}
          </div>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-[13px] font-[800] text-[#1C1A17]">
              {editing ? (isEs ? 'Editar integración' : 'Edit integration') : (isEs ? 'Nueva integración' : 'New integration')}
            </h3>
            <button
              onClick={() => {
                setShowForm(false)
                setEditing(null)
                setForm(EMPTY_FORM)
              }}
              className="text-[11px] font-[800] text-[#9E9890]"
            >
              {isEs ? 'Cerrar' : 'Close'}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <select
              value={form.provider}
              onChange={e => {
                const next = PROVIDERS.find(item => item.provider === e.target.value as Provider)
                setForm({ ...form, provider: e.target.value as Provider, type: next?.type || form.type })
              }}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]"
            >
              {PROVIDERS.map(provider => (
                <option key={provider.provider} value={provider.provider}>
                  {isEs ? provider.nameEs : provider.nameEn}
                </option>
              ))}
            </select>
            <select
              value={form.type}
              onChange={e => setForm({ ...form, type: e.target.value as IntegrationFormState['type'] })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]"
            >
              <option value="ACCOUNTING">ACCOUNTING</option>
              <option value="SPEND">SPEND</option>
              <option value="PM">PM</option>
              <option value="EXPORT">EXPORT</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="password"
              placeholder={isEs ? 'Access token' : 'Access token'}
              value={form.accessToken}
              onChange={e => setForm({ ...form, accessToken: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]"
            />
            <input
              type="password"
              placeholder={isEs ? 'Refresh token (opcional)' : 'Refresh token (optional)'}
              value={form.refreshToken}
              onChange={e => setForm({ ...form, refreshToken: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]"
            />
            <input
              placeholder={isEs ? 'External ID (opcional)' : 'External ID (optional)'}
              value={form.externalId}
              onChange={e => setForm({ ...form, externalId: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]"
            />
            <input
              placeholder={isEs ? 'Workspace / company ID (opcional)' : 'Workspace / company ID (optional)'}
              value={form.workspaceId}
              onChange={e => setForm({ ...form, workspaceId: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <textarea
              rows={5}
              placeholder={isEs ? 'Mapping JSON (opcional)' : 'Mapping JSON (optional)'}
              value={form.mappingJson}
              onChange={e => setForm({ ...form, mappingJson: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] font-mono"
            />
            <textarea
              rows={5}
              placeholder={isEs ? 'Metadata JSON (opcional)' : 'Metadata JSON (optional)'}
              value={form.metadataJson}
              onChange={e => setForm({ ...form, metadataJson: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] font-mono"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => saveMutation.mutate()}
              disabled={!form.accessToken || saveMutation.isPending}
              className="flex items-center gap-1.5 bg-[#06539B] hover:bg-[#14457E] text-white text-[11px] font-[800] px-4 py-2 rounded-full transition-colors disabled:opacity-40"
            >
              <RefreshCw size={12} />
              {saveMutation.isPending ? (isEs ? 'Guardando...' : 'Saving...') : (isEs ? 'Guardar integración' : 'Save integration')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
